import { Injectable, effect, signal } from '@angular/core';
import { Subject } from 'rxjs';

export type MenuMode = 'static' | 'overlay' | 'horizontal';

export type ColorScheme = 'light' | 'dark' | 'dim';

export interface AppConfig {
    inputStyle: string;
    colorScheme: ColorScheme;
    componentTheme: string;
    ripple: boolean;
    menuMode: MenuMode;
    scale: number;
    menuTheme: ColorScheme;
    topbarTheme: string;
}

interface LayoutState {
    staticMenuMobileActive: boolean;
    overlayMenuActive: boolean;
    staticMenuDesktopInactive: boolean;
    configSidebarVisible: boolean;
    menuHoverActive: boolean;
    topbarMenuActive: boolean;
    sidebarActive: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class LayoutService {
    _config: AppConfig = {
        ripple: true,
        inputStyle: 'outlined',
        menuMode: 'static',
        colorScheme: 'light',
        componentTheme: 'denim',
        /* Deve restare uguale a $scale in layout/layout.scss: questo valore
           vince, perche' changeScale() lo scrive inline sull'elemento html. */
        scale: 16,
        menuTheme: 'dim',
        topbarTheme: 'light',
    };

    state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
        topbarMenuActive: false,
        sidebarActive: false,
    };

    config = signal<AppConfig>(this._config);

    private configUpdate = new Subject<AppConfig>();

    private overlayOpen = new Subject<any>();

    private topbarMenuOpen = new Subject<any>();

    private menuProfileOpen = new Subject<any>();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();

    topbarMenuOpen$ = this.topbarMenuOpen.asObservable();

    menuProfileOpen$ = this.menuProfileOpen.asObservable();

    constructor() {
        this.ascoltaTelefono();

        effect(() => {
            const config = this.config();
            if (this.updateStyle(config)) {
                this.changeTheme();
            }

            this.changeScale(config.scale);
            this.onConfigUpdate();
        });
    }

    updateStyle(config: AppConfig) {
        return (
            config.componentTheme !== this._config.componentTheme ||
            config.colorScheme !== this._config.colorScheme
        );
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.state.overlayMenuActive = !this.state.overlayMenuActive;

            if (this.state.overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.state.staticMenuDesktopInactive =
                !this.state.staticMenuDesktopInactive;
        } else {
            this.state.staticMenuMobileActive =
                !this.state.staticMenuMobileActive;

            if (this.state.staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }


    onOverlaySubmenuOpen() {
        this.overlayOpen.next(null);
    }

    showConfigSidebar() {
        this.state.configSidebarVisible = true;
    }

    isOverlay() {
        return this.config().menuMode === 'overlay';
    }

    isDesktop() {
        // Stessa soglia di $breakpoint in layout.scss (1201px): se si cambia
        // qui va cambiata anche li', altrimenti il menu e il contenuto
        // ragionano su due larghezze diverse.
        return window.innerWidth > 1200;
    }

    isHorizontal() {
        return this.config().menuMode === 'horizontal';
    }

    isMobile() {
        return !this.isDesktop();
    }

    /**
     * Telefono, alla soglia dei 576px.
     *
     * NON e' `isMobile()`: quella sta a 1200px ed e' la soglia del **menu**,
     * cioe' quando la barra laterale si ritira. Questa e' la soglia della
     * **regola dei pulsanti** (`sm:` di PrimeFlex, vedi «LARGHEZZA DEI
     * PULSANTI» in _restyle-2026.scss) ed e' quella a cui vanno allineate le
     * tabelle, che altrimenti si impilerebbero ai 640px predefiniti di
     * PrimeNG: tre soglie diverse per «telefono» erano una di troppo.
     *
     * E' un segnale e non una funzione perche' serve nei template: cambia da
     * sola quando la finestra passa la soglia, senza ascoltare `resize`.
     */
    readonly telefono = signal(false);

    private ascoltaTelefono() {
        // `matchMedia` non esiste nel rendering lato server: senza questa
        // guardia il servizio non si costruisce affatto.
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }
        const query = window.matchMedia('(max-width: 575px)');
        this.telefono.set(query.matches);
        query.addEventListener('change', (e) => this.telefono.set(e.matches));
    }

    onConfigUpdate() {
        this._config = { ...this.config() };
        this.configUpdate.next(this.config());
    }

    changeTheme() {
        let { colorScheme, componentTheme } = this.config();

        const themeLink = <HTMLLinkElement>(
            document.getElementById('theme-link')
        );

        const themeLinkHref = themeLink.getAttribute('href')!;
        const newHref = themeLinkHref
            .split('/')
            .map((el) =>
                el == this._config.componentTheme
                    ? (el = componentTheme)
                    : el == `theme-${this._config.colorScheme}`
                    ? (el = `theme-${colorScheme}`)
                    : el
            )
            .join('/');

        this.replaceThemeLink(newHref);
    }

    replaceThemeLink(href: string) {
        const id = 'theme-link';
        let themeLink = <HTMLLinkElement>document.getElementById(id);
        const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

        cloneLinkElement.setAttribute('href', href);
        cloneLinkElement.setAttribute('id', id + '-clone');

        themeLink.parentNode!.insertBefore(
            cloneLinkElement,
            themeLink.nextSibling
        );
        cloneLinkElement.addEventListener('load', () => {
            themeLink.remove();
            cloneLinkElement.setAttribute('id', id);
        });
    }

    changeScale(value: number) {
        /* In percentuale, non in px. Uno stile inline in px sulla radice
           scavalca la dimensione di base che l'utente ha scelto nel browser,
           e rende inutili i rem: chi ingrandisce i caratteri non ottiene
           nulla. In percentuale la radice resta relativa a quella scelta, e
           tutte le misure in rem la seguono. 16 = 100%. */
        document.documentElement.style.fontSize = `${(value / 16) * 100}%`;
    }


}
